import { Box, Paper, Typography } from "@material-ui/core";
import { ArrowForward, FiberManualRecord } from "@material-ui/icons";
import { useEventCardStyles } from "@saleor/auditLogs/utils/eventCardStyles";
import { isStatusFieldUpdated } from "@saleor/auditLogs/utils/utils";
import { convertStringDeliveryType } from "@saleor/auditLogs/utils/utils";
import React from "react";

import { ShippingZoneActivityLogs_shippingZoneActivityLogs_edges_node } from "../../types/ShippingZoneActivityLogs";
import {
  didNotChangeNameOrDeliveryType,
  getChangedPriceRangeFields,
  hasDeliveryTypeChanged,
  hasPriceRangeChanged,
  hasPriceRateNameChanged,
  hasPriceRateNameDelTypeChanged,
  hasStatusChanged,
  isPriceRateUpdate,
  PriceRateUpdate
} from "../../utils/priceRateUtils";
import EventCardHeader from "../EventCardHeader";
import SeeMoreSeeLess from "../SeeMoreSeeLess";
import TimeContainer from "../TimeContainer";

interface PriceRateEventCardProps {
  event: ShippingZoneActivityLogs_shippingZoneActivityLogs_edges_node;
  eventNewValues: PriceRateUpdate;
}

enum CHANGED_VALUE_ENUM {
  OTHERS = 0,
  PRICE_ONLY = 1,
  VALUE_RANGE = 2
}

const PriceRateEventCard: React.FC<PriceRateEventCardProps> = props => {
  const { event, eventNewValues } = props;
  const classes = useEventCardStyles({});

  const [showActivity, setShowActivity] = React.useState(true);
  const [spliceLength, setSpliceLength] = React.useState(3);

  let deliveryType = "";
  let eventName = "";
  let eventStatus = "";
  let eventParameters: any[] = [];

  if (isPriceRateUpdate(eventNewValues)) {
    deliveryType = eventNewValues.deliveryType
      ? convertStringDeliveryType(eventNewValues.deliveryType)
      : "";

    if (eventNewValues && eventNewValues?.parameters) {
      const filteredEventParams = eventNewValues?.parameters.filter(
        param => JSON.stringify(param.old) !== JSON.stringify(param.new)
      );
      eventParameters = filteredEventParams || [];
    }

    const statusWasChanged = isStatusFieldUpdated(eventParameters);
    if (
      statusWasChanged ||
      event.action.includes("DELETE") ||
      event.action.includes("CREATE")
    ) {
      eventStatus = event.action.includes("DELETE")
        ? "DELETED"
        : eventNewValues?.newStatus === "ACT"
        ? "ACTIVE"
        : eventNewValues?.newStatus;
    }

    const nameIndicator = didNotChangeNameOrDeliveryType(eventParameters);
    eventName = nameIndicator
      ? deliveryType + " (" + eventNewValues?.rateName + ")"
      : "";
  }

  const eventStatusClass =
    eventStatus === "ACTIVE"
      ? `${classes.eventStatus} ${classes.eventStatusGreen}`
      : eventStatus === "DELETED"
      ? `${classes.eventStatus} ${classes.eventStatusRed}`
      : classes.eventStatus;

  const getChangedValue = parameter => {
    const changed = getChangedPriceRangeFields(parameter.old, parameter.new);

    if (changed.length === 1 && changed[0] === "priceAmount") {
      return CHANGED_VALUE_ENUM.PRICE_ONLY;
    }
    if (
      !changed.includes("priceAmount") &&
      (changed.includes("maximumOrderPrice") ||
        changed.includes("minimumOrderPrice"))
    ) {
      return CHANGED_VALUE_ENUM.VALUE_RANGE;
    }

    return CHANGED_VALUE_ENUM.OTHERS;
  };

  const formatNoLimit = (amount: string) =>
    amount === ""
      ? ""
      : amount === "0" || amount === "0.00" || amount === "None"
      ? "No Limit"
      : `PHP ${amount}`;

  const getPriceRateDisplay = (
    parameter,
    paramNew,
    paramOld,
    displayComparison = false
  ) => {
    if (parameter?.action.includes("delete")) {
      return <>{formatNoLimit(paramOld || "")}</>;
    } else if (displayComparison && paramNew !== paramOld) {
      return (
        <>
          <span className={classes.textGray}>
            {formatNoLimit(paramOld || "")}
          </span>{" "}
          <ArrowForward className={classes.rowArrowIcon} />
          {formatNoLimit(paramNew || "")}
        </>
      );
    } else {
      return <>{formatNoLimit(paramNew || paramOld || "")}</>;
    }
  };

  const formatFreeShipping = (amount: string) =>
    amount === ""
      ? ""
      : amount === "0" || amount === "0.00"
      ? "Free Shipping"
      : `PHP ${amount}`;

  const getPriceAmountDisplay = (parameter, displayComparison = false) => {
    if (parameter?.action.includes("delete")) {
      return <>{formatFreeShipping(parameter?.old?.priceAmount || "")}</>;
    } else if (displayComparison) {
      return (
        <>
          <span className={classes.textGray}>
            {formatFreeShipping(parameter?.old?.priceAmount || "")}
          </span>{" "}
          <ArrowForward className={classes.rowArrowIcon} />
          {formatFreeShipping(parameter?.new?.priceAmount || "")}
        </>
      );
    } else {
      return <>{formatFreeShipping(parameter?.new?.priceAmount || "")}</>;
    }
  };

  const parseStatus = (str: string) =>
    str.toUpperCase() === "ACT" ? "ACTIVE" : str;

  const isStatusFieldPresent = (param): boolean =>
    "status" in (param?.old || {}) && "status" in (param?.new || {});

  const hideEventItems =
    (eventParameters.length > 0 && isStatusFieldPresent(eventParameters[0])) ||
    event.action.includes("DELETE");

  const renderRateName = parameter => (
    <div className={classes.rowLi}>
      <span className={classes.rowLabel}>Rate Name:</span>

      {parameter?.action.includes("update") ? (
        <>
          <span className={classes.textGray}>
            {parameter?.old?.name || parameter?.old?.rateName}
          </span>{" "}
          <ArrowForward className={classes.rowArrowIcon} />{" "}
          {parameter?.new?.name || parameter?.new?.rateName}
        </>
      ) : (
        <>{parameter?.new?.name || parameter?.new?.rateName}</>
      )}
    </div>
  );

  const renderDeliveryType = parameter => (
    <div className={classes.rowLi}>
      <span className={classes.rowLabel}>Delivery Type:</span>

      {parameter?.action.includes("update") ? (
        <>
          <span className={classes.textGray}>
            {convertStringDeliveryType(
              parameter?.old?.deliveryType || parameter?.old?.delivery_type
            )}
          </span>{" "}
          <ArrowForward className={classes.rowArrowIcon} />{" "}
          {convertStringDeliveryType(
            parameter?.new?.deliveryType || parameter?.new?.delivery_type
          )}
        </>
      ) : (
        <>
          {convertStringDeliveryType(
            parameter?.new?.deliveryType || parameter?.new?.delivery_type
          )}
        </>
      )}
    </div>
  );

  const renderStatus = parameter => (
    <div className={classes.rowLi}>
      <span className={classes.rowLabel}>Status:</span>

      {parameter?.action.includes("update") ? (
        <>
          <span className={classes.textGray}>
            {parseStatus(parameter?.old?.status)}
          </span>{" "}
          <ArrowForward className={classes.rowArrowIcon} />{" "}
          {parseStatus(parameter?.new?.status)}
        </>
      ) : (
        <>{parseStatus(parameter?.new?.status)}</>
      )}
    </div>
  );

  const renderPrice = (parameter, priceDisplayFlag) => (
    <div className={classes.rowLi}>
      <span className={classes.rowLabel}>Rate Price:</span>{" "}
      {getPriceAmountDisplay(parameter, priceDisplayFlag)}
    </div>
  );

  const renderValueRange = (parameter, displayFlag) => (
    <>
      <li>
        <div className={classes.rowLi}>
          <span className={classes.rowLabel}>Minimum Order Value:</span>{" "}
          {getPriceRateDisplay(
            parameter,
            parameter?.new?.minimumOrderPrice,
            parameter?.old?.minimumOrderPrice,
            displayFlag
          )}
        </div>
      </li>
      <li>
        <div className={classes.rowLi}>
          <span className={classes.rowLabel}>Maximum Order Value:</span>{" "}
          {getPriceRateDisplay(
            parameter,
            parameter?.new?.maximumOrderPrice,
            parameter?.old?.maximumOrderPrice,
            displayFlag
          )}
        </div>
      </li>
    </>
  );

  return (
    <Box className={classes.timelineBox}>
      <Box className={classes.timelineContainer}>
        <FiberManualRecord className={`${classes.dot} [status]`} />
        <Box className={classes.timelineItem}>
          <TimeContainer
            dateTimeStamp={event.createdAt}
            eventStatus={eventStatus}
            eventStatusClass={eventStatusClass}
            showDot={eventStatus === "ACTIVE" || eventStatus === "INACTIVE"}
          />

          <div style={{ width: "100%" }}>
            <EventCardHeader
              eventName={eventName}
              eventUser={event?.doer?.email}
              eventUserActionType={
                event.action.includes("CREATE") ? "created by" : "updated by"
              }
              showEventItems={!hideEventItems}
              showActivity={showActivity}
              action={() => setShowActivity(!showActivity)}
            />

            {showActivity && (
              <Paper className={classes.paper}>
                {!hideEventItems &&
                  eventParameters
                    .slice(0, spliceLength)
                    .map((parameter, index) => {
                      const changedValue = getChangedValue(parameter);
                      const hasPriceRangeChangedFlag = hasPriceRangeChanged(
                        parameter
                      );
                      const hasStatusChangedFlag = hasStatusChanged(parameter);
                      const hasPriceRateNameChangedFlag = hasPriceRateNameChanged(
                        parameter
                      );
                      const hasDeliveryTypeChangedFlag = hasDeliveryTypeChanged(
                        parameter
                      );
                      const hasPriceRateNameDelTypeChangedFlag = hasPriceRateNameDelTypeChanged(
                        parameter
                      );

                      return (
                        <div
                          key={`auditLogsPriceEventItemCard-${index}`}
                          className={`${classes.updateRow} ${
                            (index + 1) % 2 === 1 ? classes.rowBg : ""
                          }`}
                        >
                          {hasPriceRateNameChangedFlag &&
                          !hasDeliveryTypeChangedFlag ? (
                            <div
                              className={`${classes.rowBadge} ${classes.rowBadgeInfo}`}
                            >
                              {deliveryType}
                            </div>
                          ) : !hasPriceRateNameChangedFlag &&
                            hasDeliveryTypeChangedFlag ? (
                            <div
                              className={`${classes.rowBadge} ${classes.rowBadgeInfo}`}
                            >
                              {eventNewValues?.rateName}
                            </div>
                          ) : hasPriceRangeChangedFlag &&
                            changedValue === CHANGED_VALUE_ENUM.VALUE_RANGE ? (
                            <div
                              className={`${classes.rowBadge} ${classes.rowBadgeInfo}`}
                            >
                              Value Range
                            </div>
                          ) : hasPriceRangeChangedFlag &&
                            changedValue === CHANGED_VALUE_ENUM.PRICE_ONLY ? (
                            <div
                              className={`${classes.rowBadge} ${classes.rowBadgeInfo}`}
                            >
                              Rate
                            </div>
                          ) : (
                            <></>
                          )}

                          <Typography>
                            <ul>
                              {hasPriceRateNameDelTypeChangedFlag ? (
                                <>
                                  <li>{renderRateName(parameter)}</li>
                                  <li>{renderDeliveryType(parameter)}</li>
                                </>
                              ) : hasPriceRateNameChangedFlag ? (
                                <li>{renderRateName(parameter)}</li>
                              ) : hasDeliveryTypeChangedFlag ? (
                                <li>{renderDeliveryType(parameter)}</li>
                              ) : (
                                <></>
                              )}

                              {hasStatusChangedFlag && (
                                <li>{renderStatus(parameter)}</li>
                              )}

                              {hasPriceRangeChangedFlag &&
                              changedValue === CHANGED_VALUE_ENUM.PRICE_ONLY ? (
                                <li>
                                  {renderPrice(
                                    parameter,
                                    parameter?.action.includes("update")
                                  )}
                                </li>
                              ) : hasPriceRangeChangedFlag &&
                                changedValue ===
                                  CHANGED_VALUE_ENUM.VALUE_RANGE ? (
                                <>
                                  {renderValueRange(
                                    parameter,
                                    parameter?.action.includes("update")
                                  )}
                                </>
                              ) : hasPriceRangeChangedFlag ? (
                                <>
                                  <li>
                                    {renderPrice(
                                      parameter,
                                      parameter?.action.includes("update")
                                    )}
                                  </li>
                                  {renderValueRange(
                                    parameter,
                                    parameter?.action.includes("update")
                                  )}
                                </>
                              ) : (
                                <></>
                              )}
                            </ul>
                          </Typography>
                        </div>
                      );
                    })}

                {eventParameters.length > 3 && (
                  <SeeMoreSeeLess
                    totalLength={eventParameters.length}
                    spliceLength={spliceLength}
                    setSpliceLength={setSpliceLength}
                  />
                )}
              </Paper>
            )}
          </div>
        </Box>
      </Box>
    </Box>
  );
};

export default PriceRateEventCard;
